import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
  ChangeEvent,
} from "react";
import { withRouter, RouteComponentProps } from "react-router";
import { FormattedMessage } from "react-intl";
import routes from "../app/routes";
import { Button } from "@cmsgov/ds-medicare-gov";
import PlanQuestions from "../pages/QuestionRoutingPage/PlanQuestions";
import { CoverageType } from "../pages/QuestionRoutingPage/types";
import {
  parseSearchParams,
  useTranslate,
  getPlanTypeForCoverageType,
} from "../helpers";
import { ActionType, PlanType, County } from "../@types";
import CountyPicker from "./CountyPicker";
import { AppStore } from "../app/store";
import URI from "urijs";
import { getCounty } from "../api";
import { stringIsValidPlanType } from "../helpers/planTypes";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../app/contexts/Analytics/types";
import { MandatoryDialog } from "../components";

enum MissingValue {
  PLAN_TYPE = "plan_type",
  FIPS = "fips",
  ZIP = "zip",
}

const StateValidator: FunctionComponent<RouteComponentProps> = ({
  location,
  history,
  children,
}) => {
  const t = useTranslate();
  const { state, dispatch } = useContext(AppStore.AppContext);
  const [showModal, setShowModal] = useState(false);
  const [coverageSelection, setCoverageSelection] = useState<CoverageType>(
    CoverageType.selectionNotMade
  );
  const [newLocation, setNewLocation] = useState<{
    county: County;
    zipcode: string;
  } | null>(null);
  const [missingValues, setMissingValues] = useState<MissingValue[]>([]);
  const [missingValue, setMissingValue] = useState<MissingValue>();
  const [title, setTitle] = useState<JSX.Element | string>("");

  const handleLocationSelection = (county?: County, zipcode?: string): void => {
    if (county && county.fips && zipcode) {
      setNewLocation({ county, zipcode });
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "Missing Routing Info Modal - Zip Code",
          event_label: "Enter Zip Code",
        },
      });
    } else {
      setNewLocation(null);
    }
  };

  const updatePlanType = (): void => {
    dispatch({
      type: ActionType.UPDATE_PLAN_TYPE,
      payload: getPlanTypeForCoverageType(coverageSelection),
    });

    dispatch({
      type: ActionType.UPDATE_VIEW_MEDIGAP,
      payload: coverageSelection === CoverageType.PDPAndMedigap,
    });

    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "Missing Routing Info Modal - Type of Coverage",
        event_label: "Continue",
      },
    });
  };

  const updateLocation = (): void => {
    if (newLocation) {
      const { county, zipcode } = newLocation;

      dispatch({ type: ActionType.UPDATE_COUNTY, payload: county });
      dispatch({ type: ActionType.UPDATE_FIPS, payload: county.fips });
      dispatch({ type: ActionType.UPDATE_ZIPCODE, payload: zipcode });
      dispatch({ type: ActionType.UPDATE_PHARMACY_ADDRESS, payload: "" });

      setNewLocation(null);

      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "Missing Routing Info Modal - Zip Code",
          event_label: "Continue",
        },
      });
    }
  };

  const updateUrl = (): void => {
    const updatedUrl = new URI(location.pathname);

    if (state.planType) {
      updatedUrl.addSearch("plan_type", state.planType);
    }

    if (state.fips) {
      updatedUrl.addSearch("fips", state.fips);
    }

    if (state.zipcode) {
      updatedUrl.addSearch("zip", state.zipcode);
    }

    history.replace(updatedUrl.toString());
  };

  useEffect(() => {
    if (missingValues.length) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }

    const value = missingValues[0];

    setMissingValue(value);

    if (value === MissingValue.FIPS || value === MissingValue.ZIP) {
      setTitle(t("state_validator.enter_zip"));
    } else if (value === MissingValue.PLAN_TYPE) {
      setTitle(
        <FormattedMessage
          id="state_validator.what_type_of_coverage_for_year"
          values={{ year: state.year }}
        />
      );
    }
  }, [missingValues]);

  const validatePlanType = (
    missingValues: MissingValue[],
    plan_type: string
  ): MissingValue[] => {
    let planType = "";

    if (plan_type) {
      planType = plan_type.toUpperCase();
    }

    if (stringIsValidPlanType(planType)) {
      planType = planType as PlanType;
    } else {
      planType = "";
    }

    if (planType && planType !== state.planType) {
      dispatch({
        type: ActionType.UPDATE_PLAN_TYPE,
        payload: planType,
      });

      missingValues = missingValues.filter(v => v !== MissingValue.PLAN_TYPE);
    } else if (!planType && state.planType) {
      updateUrl();

      missingValues = missingValues.filter(v => v !== MissingValue.PLAN_TYPE);
    } else if (
      !planType &&
      !state.planType &&
      !missingValues.includes(MissingValue.PLAN_TYPE)
    ) {
      missingValues.push(MissingValue.PLAN_TYPE);
    }

    return missingValues;
  };

  const validateLocation = (
    missingValues: MissingValue[],
    fips: string,
    zip: string
  ): MissingValue[] => {
    if (!state.fips && fips) {
      dispatch({
        type: ActionType.UPDATE_FIPS,
        payload: fips,
      });

      getCounty(fips).then(county =>
        dispatch({ type: ActionType.UPDATE_COUNTY, payload: county })
      );

      missingValues = missingValues.filter(v => v !== MissingValue.FIPS);
    } else if (!fips && state.fips) {
      updateUrl();

      missingValues = missingValues.filter(v => v !== MissingValue.FIPS);
    } else if (
      !fips &&
      !state.fips &&
      !missingValues.includes(MissingValue.FIPS)
    ) {
      missingValues.push(MissingValue.FIPS);
    }

    if (!state.zipcode && zip) {
      dispatch({
        type: ActionType.UPDATE_ZIPCODE,
        payload: zip,
      });

      missingValues = missingValues.filter(v => v !== MissingValue.ZIP);
    } else if (!zip && state.zipcode) {
      updateUrl();

      missingValues = missingValues.filter(v => v !== MissingValue.ZIP);
    } else if (
      !zip &&
      !state.zipcode &&
      !missingValues.includes(MissingValue.ZIP)
    ) {
      missingValues.push(MissingValue.ZIP);
    }

    return missingValues;
  };

  const validateState = (): void => {
    const url = new URI(location.search);
    const { plan_type, fips, zip } = parseSearchParams(url.search(true));

    let values = [...missingValues];

    if (
      location.pathname === routes.pharmacy ||
      location.pathname.includes(routes.inNetworkPharmacy.replace(":id", ""))
    ) {
      values = validateLocation(values, fips, zip);
    } else if (location.pathname === routes.searchResults) {
      values = validatePlanType(values, plan_type);
      values = validateLocation(values, fips, zip);
    } else if (location.pathname === routes.sanctionedPlans) {
      values = validatePlanType(values, plan_type);
      values = validateLocation(values, fips, zip);
    } else if (location.pathname === routes.medigap.plans) {
      values = validateLocation(values, fips, zip);
    } else if (
      location.pathname.includes(
        routes.medigap.planDetails.replace(":medigapPlanType", "")
      ) ||
      location.pathname.includes(
        routes.medigap.policies.replace(":medigapPlanType", "")
      )
    ) {
      values = validateLocation(values, fips, zip);
    } else if (location.pathname === routes.coverageWizard.info) {
      values = validateLocation(values, fips, zip);
    }

    setMissingValues(values);
  };

  useEffect(validateState, [state, location.pathname, location.search]);

  return (
    <>
      {showModal && missingValue && (
        <MandatoryDialog
          className="StateValidator__modal"
          data-cy="e2e-state-validator"
          onExit={(): void => setShowModal(false)}
          heading={title}
          closeButtonText=""
        >
          {[MissingValue.ZIP, MissingValue.FIPS].includes(missingValue) && (
            <>
              <p className="ChangeLocationModal__message">
                {t("state_validator.zip_tailor")}
              </p>
              <CountyPicker
                label={t("pharmacy_selection.change_location.label")}
                onCountySelect={handleLocationSelection}
                className="ds-u-margin-bottom--2"
                initialZip={state.zipcode}
              />
              <Button
                className="ds-u-margin-right--2 e2e-state-validator-continue"
                key="primary"
                variation="solid"
                onClick={() => {
                  updateLocation();
                  dispatch({
                    type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                    settings: {
                      button: {
                        buttonStyle: AnalyticsButtonStyle.PRIMARY,
                        buttonType: AnalyticsButtonType.BUTTON,
                        text: t("state_validator.continue"),
                      },
                    },
                  });
                }}
                disabled={!newLocation}
              >
                {t("state_validator.continue")}
              </Button>
            </>
          )}

          {missingValue === MissingValue.PLAN_TYPE && (
            <>
              <PlanQuestions
                handleChoice={(e: ChangeEvent<HTMLInputElement>): void => {
                  setCoverageSelection(e.target.value as CoverageType);
                  dispatch({
                    type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                    settings: {
                      event_action:
                        "Missing Routing Info Modal - Type of Coverage",
                      event_label: e.target.value,
                    },
                  });
                }}
                coverageSelection={coverageSelection}
                hideMedigapButton={true}
              />
              <Button
                className="ds-u-margin-right--2 ds-u-margin-top--2"
                key="primary"
                variation="solid"
                onClick={() => {
                  updatePlanType();
                  dispatch({
                    type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                    settings: {
                      button: {
                        buttonStyle: AnalyticsButtonStyle.PRIMARY,
                        buttonType: AnalyticsButtonType.BUTTON,
                        text: t("state_validator.continue"),
                      },
                    },
                  });
                }}
                disabled={coverageSelection === CoverageType.selectionNotMade}
              >
                {t("state_validator.continue")}
              </Button>
            </>
          )}
        </MandatoryDialog>
      )}
      {children}
    </>
  );
};

export default withRouter(StateValidator);
